<script setup lang="ts">
import { withTrailingSlash } from 'ufo'
import { toArticles } from '~/types/article'

const RECENT_COUNT = 5

const { data: _articles } = await useAsyncData(
  'recentPost',
  async () => {
    const rows = await queryContent(withTrailingSlash('/posts')).sort({ date: -1 }).find()
    return toArticles(rows.slice(0, RECENT_COUNT))
  },
)

const articles = computed(() => _articles.value || [])
</script>

<template>
  <div v-if="articles.length" class="mb-14 mt-0 md:mb-16 not-prose">
    <header class="mb-10 border-l-4 border-primary-500 pl-4 md:mb-12 md:pl-5">
      <h2 class="text-2xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-3xl">
        Latest posts
      </h2>
    </header>
    <ol class="m-0 list-none p-0">
      <li
        v-for="(article, index) in articles"
        :key="article._path"
        class="relative flex gap-4 pb-12 last:pb-0 sm:gap-6"
      >
        <div class="w-[4.5rem] shrink-0 text-right sm:w-28">
          <time
            :datetime="article.date"
            class="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 sm:text-sm sm:normal-case sm:tracking-normal sm:font-medium"
          >
            {{ formatDate(article.date) }}
          </time>
        </div>

        <div class="relative flex w-6 shrink-0 justify-center">
          <div
            v-if="index < articles.length - 1"
            class="absolute left-1/2 top-[0.875rem] bottom-0 w-px -translate-x-1/2 bg-gray-200 dark:bg-gray-700"
            aria-hidden="true"
          />
          <div
            class="relative z-10 mt-1 h-3 w-3 shrink-0 rounded-full border-2 border-white bg-primary-500 shadow-sm ring-2 ring-gray-200 dark:border-gray-950 dark:ring-gray-700"
            aria-hidden="true"
          />
        </div>

        <article class="min-w-0 flex-1 pt-0">
          <NuxtLink
            :to="article._path"
            class="text-lg font-semibold text-gray-900 decoration-primary-500 decoration-2 underline-offset-4 transition-colors hover:text-primary-500 dark:text-white dark:hover:text-primary-400 sm:text-xl"
          >
            {{ article.title }}
          </NuxtLink>
          <p
            v-if="article.description"
            class="mt-2 text-sm leading-relaxed text-gray-600 dark:text-gray-400 line-clamp-2"
          >
            {{ article.description }}
          </p>
        </article>
      </li>
    </ol>
  </div>
</template>
